/**
 * ALTIORA · Knowledge Graph Visualiser
 * ---------------------------------------------------------------
 * Outil d'administration, de recherche et de maintenance du patrimoine
 * de connaissances.
 *
 * Fonctionnalités :
 *  - Rendu interactif du graphe (React Flow — zoom/pan/drag)
 *  - Filtres par facette (15) et par type de source
 *  - Panneau d'inspection du nœud sélectionné
 *  - Détecteurs automatiques : orphelins, doublons, incohérences
 *  - Recherche textuelle en overlay
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import ReactFlow, {
  Background, Controls, MiniMap,
  applyNodeChanges, applyEdgeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { toast } from "sonner";
import {
  Search, Filter, AlertTriangle, GitFork, RefreshCw, Info, X,
  Copy, ShieldAlert,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, PrimaryButton, GhostButton, FormField,
  Input, Loader, NAVY, CHAMPAGNE, SLATE, NAVY_DARK as INK,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

const SOURCE_COLORS = {
  regulation_official:    "#8B4E00",  // AMF/ARSF - brun officiel
  institution_doc:        "#0a2540",  // navy
  partner_doc:            "#2b57a8",
  professional_literature:"#c9a34e",
  internal_procedure:     "#6b7c93",
  best_practice:          "#0a7f42",
  field_experience:       "#527a41",
  field_observation:      "#527a41",
  jurisprudence:          "#7a1e1e",
  altiora_original:       "#c9a34e",
};

const SOURCE_LABELS = {
  regulation_official: "Réglementaire", institution_doc: "Institution",
  partner_doc: "Partenaire", professional_literature: "Littérature pro",
  internal_procedure: "Procédure interne", best_practice: "Bonne pratique",
  field_experience: "Retour d'expérience", field_observation: "Observation",
  jurisprudence: "Jurisprudence", altiora_original: "ALTIORA",
};

// Layout en grille (fallback simple si pas de coordonnées)
function gridLayout(nodes) {
  const cols = Math.ceil(Math.sqrt(nodes.length));
  return nodes.map((n, i) => ({
    id: n.id,
    data: { label: n.title, node: n },
    position: { x: (i % cols) * 220, y: Math.floor(i / cols) * 100 },
    style: {
      background: "#fff",
      border: `2px solid ${SOURCE_COLORS[n.source_type] || "#6b7c93"}`,
      borderRadius: 8,
      padding: 6,
      fontSize: 10,
      width: 180,
      color: NAVY,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    },
    "data-testid": `graph-node-${n.id}`,
  }));
}

export default function AdminKnowledgeGraph() {
  const [schema, setSchema] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterFacetKey, setFilterFacetKey] = useState("");
  const [filterFacetValue, setFilterFacetValue] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [rawNodes, setRawNodes] = useState([]);   // items KE bruts
  const [rawEdges, setRawEdges] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [searchQ, setSearchQ] = useState("");

  const loadGraph = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filterSource) params.set("source_type", filterSource);
      if (filterFacetKey && filterFacetValue) {
        params.set("facet_key", filterFacetKey);
        params.set("facet_value", filterFacetValue);
      }
      const { data } = await api.get(
        `/knowledge-engine/analytics/graph-explorer?${params}`
      );
      setRawNodes(data.nodes || []);
      setRawEdges(data.edges || []);
      setNodes(gridLayout(data.nodes || []));
      setEdges((data.edges || []).map((e, i) => ({
        id: `${e.source}-${e.target}-${e.type}-${i}`,
        source: e.source, target: e.target,
        label: e.type,
        labelStyle: { fontSize: 8, fill: SLATE },
        style: { stroke: "#c9a34e", strokeWidth: 1 },
        animated: e.type === "requires" || e.type === "depends_on",
      })));
    } catch {
      toast.error("Impossible de charger le graphe");
    } finally {
      setLoading(false);
    }
  };

  const loadSchema = async () => {
    const { data } = await api.get("/knowledge-engine/facets");
    setSchema(data);
  };

  const loadAnalytics = async () => {
    try {
      const [o, d, i] = await Promise.all([
        api.get("/knowledge-engine/analytics/orphans"),
        api.get("/knowledge-engine/analytics/duplicates"),
        api.get("/knowledge-engine/analytics/inconsistencies"),
      ]);
      setAnalytics({ orphans: o.data, duplicates: d.data, inconsistencies: i.data });
      setShowAnalytics(true);
    } catch { toast.error("Impossible de charger les analyses"); }
  };

  useEffect(() => { loadSchema(); loadGraph(); }, []);

  const onNodesChange = useCallback((chg) =>
    setNodes((ns) => applyNodeChanges(chg, ns)), []);
  const onEdgesChange = useCallback((chg) =>
    setEdges((es) => applyEdgeChanges(chg, es)), []);
  const onNodeClick = useCallback((_, node) => setSelectedNodeId(node.id), []);

  const selectedNode = useMemo(() =>
    rawNodes.find((n) => n.id === selectedNodeId), [rawNodes, selectedNodeId]);

  // Recherche : highlight sur les nodes matchant le texte
  const filteredNodes = useMemo(() => {
    if (!searchQ.trim()) return nodes;
    const q = searchQ.toLowerCase();
    return nodes.map((n) => ({
      ...n,
      style: {
        ...n.style,
        borderWidth: (n.data.label || "").toLowerCase().includes(q) ? 4 : 2,
        opacity: (n.data.label || "").toLowerCase().includes(q) ? 1 : 0.35,
      },
    }));
  }, [nodes, searchQ]);

  return (
    <PageShell>
      <TopBar />
      <div className="max-w-[1600px] mx-auto px-6 py-6" data-testid="admin-kg-root">
        <PageHeader
          eyebrow="ALTIORA · Administration"
          title="Knowledge Graph Visualiser"
          subtitle="Cartographiez, inspectez et maintenez le patrimoine de connaissances."
          testid="admin-kg-header"
        />

        {/* Barre de filtres */}
        <Card className="p-3 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
            <FormField label="Recherche" testid="admin-kg-ff-q">
              {(a) => <Input testid="admin-kg-search" placeholder="Titre du nœud…"
                             value={searchQ}
                             onChange={(e) => setSearchQ(e.target.value)} {...a} />}
            </FormField>
            <FormField label="Type de source" testid="admin-kg-ff-src">
              {() => (
                <select data-testid="admin-kg-filter-source"
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={filterSource}
                        onChange={(e) => setFilterSource(e.target.value)}>
                  <option value="">Tous</option>
                  {schema?.source_types?.map((t) => (
                    <option key={t} value={t}>{SOURCE_LABELS[t] || t}</option>
                  ))}
                </select>
              )}
            </FormField>
            <FormField label="Facette (clé)" testid="admin-kg-ff-fk">
              {() => (
                <select data-testid="admin-kg-filter-fkey"
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={filterFacetKey}
                        onChange={(e) => setFilterFacetKey(e.target.value)}>
                  <option value="">—</option>
                  {schema?.facet_keys?.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              )}
            </FormField>
            <FormField label="Facette (valeur)" testid="admin-kg-ff-fv">
              {(a) => <Input testid="admin-kg-filter-fval"
                             placeholder="ex: reer, CA-QC, retraite_proche"
                             value={filterFacetValue}
                             onChange={(e) => setFilterFacetValue(e.target.value)} {...a} />}
            </FormField>
            <div className="flex items-end gap-2">
              <PrimaryButton onClick={loadGraph} testid="admin-kg-apply">
                <Filter size={12} /> Appliquer
              </PrimaryButton>
              <GhostButton onClick={loadAnalytics} testid="admin-kg-analytics">
                <AlertTriangle size={12} /> Analyses
              </GhostButton>
            </div>
          </div>
        </Card>

        {/* Zone graphe + panneau détail */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-3">
          <div className="lg:col-span-3">
            <Card className="p-0 relative" style={{ height: "70vh" }}>
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader size={22} />
                </div>
              ) : (
                <div data-testid="admin-kg-canvas" style={{ height: "100%" }}>
                  <ReactFlow
                    nodes={filteredNodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={onNodeClick}
                    fitView
                    minZoom={0.15}
                    maxZoom={2}
                    proOptions={{ hideAttribution: true }}
                  >
                    <Background gap={20} color="#e5e0d0" />
                    <Controls />
                    <MiniMap
                      nodeColor={(n) =>
                        SOURCE_COLORS[n.data?.node?.source_type] || "#6b7c93"}
                      style={{ background: "#faf6ea" }}
                    />
                  </ReactFlow>
                </div>
              )}
              <div className="absolute bottom-2 left-2 text-[10px] font-mono uppercase tracking-widest"
                   style={{ color: SLATE }}>
                {rawNodes.length} nœuds · {rawEdges.length} arêtes
              </div>
            </Card>
          </div>
          <div className="lg:col-span-1">
            {selectedNode ? (
              <NodeInspector
                node={selectedNode}
                edges={rawEdges.filter((e) => e.source === selectedNode.id ||
                                                e.target === selectedNode.id)}
                nodes={rawNodes}
                onClear={() => setSelectedNodeId(null)}
              />
            ) : (
              <Card className="p-4 text-center" testid="admin-kg-empty-inspector">
                <GitFork size={22} className="mx-auto mb-2" style={{ color: CHAMPAGNE }} />
                <div className="text-sm" style={{ color: NAVY }}>
                  Cliquez sur un nœud pour l'inspecter et voir ses relations.
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Analytics panel */}
        {showAnalytics && analytics && (
          <AnalyticsModal
            data={analytics}
            onClose={() => setShowAnalytics(false)}
            onOpenNode={(id) => { setSelectedNodeId(id); setShowAnalytics(false); }}
          />
        )}
      </div>
    </PageShell>
  );
}

function NodeInspector({ node, edges, nodes, onClear }) {
  const outgoing = edges.filter((e) => e.source === node.id);
  const incoming = edges.filter((e) => e.target === node.id);
  const nameOf = (id) => nodes.find((n) => n.id === id)?.title || id;
  return (
    <Card className="p-4" testid="admin-kg-inspector">
      <div className="flex justify-between items-start">
        <div className="text-[10px] font-mono uppercase tracking-widest"
             style={{ color: CHAMPAGNE }}>
          {SOURCE_LABELS[node.source_type] || node.source_type}
          {" · "} confiance {node.confidence}
        </div>
        <button onClick={onClear} className="p-0.5 rounded hover:bg-gray-100"
                data-testid="admin-kg-inspector-clear">
          <X size={12} />
        </button>
      </div>
      <h3 className="font-serif text-base mt-1" style={{ color: NAVY }}>
        {node.title}
      </h3>
      <div className="text-[10px] font-mono mt-2" style={{ color: SLATE }}>
        {node.id}
      </div>

      {Object.entries(node.facets || {}).some(([, v]) => (v || []).length) && (
        <div className="mt-3">
          <div className="text-[10px] font-mono uppercase tracking-widest"
               style={{ color: SLATE }}>Facettes</div>
          <div className="flex flex-wrap gap-1 mt-1">
            {Object.entries(node.facets || {}).map(([k, vs]) =>
              (vs || []).map((v) => (
                <span key={`${k}-${v}`}
                      className="text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: "#f3ecd7", color: NAVY,
                                border: "1px solid #d4c69e" }}>
                  {k}:{v}
                </span>
              ))
            )}
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest"
             style={{ color: SLATE }}>
          Dépendances sortantes ({outgoing.length})
        </div>
        <ul className="mt-1 space-y-1 text-xs">
          {outgoing.slice(0, 12).map((e, i) => (
            <li key={i} data-testid={`admin-kg-out-${i}`}
                className="flex items-start gap-1">
              <span className="text-[9px] font-mono uppercase"
                    style={{ color: CHAMPAGNE }}>
                {e.type}
              </span>
              <span style={{ color: INK }} className="truncate">→ {nameOf(e.target)}</span>
            </li>
          ))}
          {outgoing.length === 0 && (
            <li className="text-xs" style={{ color: SLATE }}>—</li>
          )}
        </ul>
      </div>

      <div className="mt-3">
        <div className="text-[10px] font-mono uppercase tracking-widest"
             style={{ color: SLATE }}>
          Dépendances entrantes ({incoming.length})
        </div>
        <ul className="mt-1 space-y-1 text-xs">
          {incoming.slice(0, 12).map((e, i) => (
            <li key={i} data-testid={`admin-kg-in-${i}`}
                className="flex items-start gap-1">
              <span style={{ color: INK }} className="truncate">← {nameOf(e.source)}</span>
              <span className="text-[9px] font-mono uppercase ml-auto"
                    style={{ color: CHAMPAGNE }}>
                {e.type}
              </span>
            </li>
          ))}
          {incoming.length === 0 && (
            <li className="text-xs" style={{ color: SLATE }}>—</li>
          )}
        </ul>
      </div>
    </Card>
  );
}

function AnalyticsModal({ data, onClose, onOpenNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         data-testid="admin-kg-analytics-modal"
         onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b"
             style={{ borderColor: "#e5e0d0" }}>
          <div className="flex items-center gap-2">
            <ShieldAlert size={16} style={{ color: NAVY }} />
            <h3 className="font-serif text-lg" style={{ color: NAVY }}>
              Analyses de qualité du patrimoine
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"
                  data-testid="admin-kg-analytics-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-6">
          <AnalyticsSection
            icon={GitFork} title="Items orphelins (aucune relation)"
            count={data.orphans.count} testid="admin-kg-analytics-orphans"
            emptyMsg="Aucun item orphelin détecté ✓">
            <ul className="text-xs space-y-1">
              {data.orphans.items.slice(0, 15).map((it) => (
                <li key={it.id}
                    className="p-2 rounded flex justify-between items-center"
                    style={{ background: "#faf6ea", border: "1px solid #e5d9b8" }}>
                  <div>
                    <div style={{ color: NAVY }}>{it.title}</div>
                    <div className="text-[9px] font-mono uppercase"
                         style={{ color: SLATE }}>
                      {SOURCE_LABELS[it.source_type] || it.source_type} · conf. {it.confidence}
                    </div>
                  </div>
                  <button onClick={() => onOpenNode(it.id)}
                          className="text-[10px] px-2 py-0.5 rounded"
                          style={{ background: "#f3ecd7", color: NAVY }}>
                    Inspecter
                  </button>
                </li>
              ))}
            </ul>
          </AnalyticsSection>

          <AnalyticsSection
            icon={Copy} title="Groupes de doublons potentiels"
            count={data.duplicates.count} testid="admin-kg-analytics-duplicates"
            emptyMsg="Aucun doublon suspect détecté ✓">
            <ul className="text-xs space-y-2">
              {data.duplicates.duplicates.slice(0, 15).map((g, i) => (
                <li key={i} className="p-2 rounded"
                    style={{ background: "#faf6ea", border: "1px solid #e5d9b8" }}>
                  <div className="text-[10px] font-mono uppercase"
                       style={{ color: SLATE }}>
                    Clé: {g.key} · {SOURCE_LABELS[g.source_type] || g.source_type}
                  </div>
                  <ul className="mt-1 pl-3 list-disc">
                    {g.items.map((it) => (
                      <li key={it.id} style={{ color: NAVY }}
                          className="flex justify-between">
                        <span>{it.title}</span>
                        <button onClick={() => onOpenNode(it.id)}
                                className="text-[10px] px-2 rounded ml-2"
                                style={{ background: "#f3ecd7", color: NAVY }}>
                          Inspecter
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </AnalyticsSection>

          <AnalyticsSection
            icon={AlertTriangle} title="Incohérences du graphe"
            count={data.inconsistencies.count}
            testid="admin-kg-analytics-inconsistencies"
            emptyMsg="Aucune incohérence détectée ✓">
            <ul className="text-xs space-y-1">
              {data.inconsistencies.inconsistencies.slice(0, 20).map((it, i) => (
                <li key={i} className="p-2 rounded"
                    style={{ background: "#fdecec", border: "1px solid #f3b8b8" }}>
                  <div style={{ color: "#7a1e1e" }}>{it.issue}</div>
                  <div className="text-[10px]" style={{ color: SLATE }}>
                    {it.source_title} ↔ {it.target_id}
                  </div>
                </li>
              ))}
            </ul>
          </AnalyticsSection>
        </div>
      </div>
    </div>
  );
}

function AnalyticsSection({ icon: Icon, title, count, children, testid, emptyMsg }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={13} style={{ color: CHAMPAGNE }} />
        <h4 className="text-[11px] font-mono uppercase tracking-widest"
             style={{ color: NAVY }}>
          {title} <span style={{ color: SLATE }}>({count})</span>
        </h4>
      </div>
      {count === 0
        ? <div className="text-xs pl-5" style={{ color: SLATE }}>{emptyMsg}</div>
        : children}
    </section>
  );
}
